/**
 * QuickJobForm.jsx — Fast 7-field job entry form
 *
 * Fields: Client (autocomplete + free-text), Email, Phone, Job Type,
 *         Shoot Date, Revenue (chips + manual), Location (optional).
 *
 * If client name doesn't match an existing contact, creates a stub
 * contact with data.incomplete = true.
 */

import { useState, useMemo } from 'react';
import { Box, Paper, Typography, TextField, Button, MenuItem, Select, InputLabel, FormControl, Grid, Stack, Chip, List, ListItemButton, ListItemText } from '@mui/material';
import { useCreateJob } from '../../hooks/useJobs';
import { useContacts, useCreateContact } from '../../hooks/useContacts';
import AddressAutocomplete from '../shared/AddressAutocomplete';
import { useToast } from '../layout/Toast';

const JOB_TYPES = ['Real Estate Video', 'FPV Tour', 'Matterport', 'Luxury Film', 'Event', 'Other'];
const REVENUE_PRESETS = [500, 750, 1000, 1500, 2000, 3000, 5000];

function fmtChip(v) {
  if (v >= 1000) return `$${(v / 1000).toFixed(v % 1000 === 0 ? 0 : 1)}K${v === 5000 ? '+' : ''}`;
  return `$${v}`;
}

export default function QuickJobForm({ onSave, onCancel, prefillContact }) {
  const { toast } = useToast();
  const createJob = useCreateJob();
  const createContact = useCreateContact();
  const { data: allContacts = [] } = useContacts();

  const [clientName, setClientName] = useState(prefillContact?.clientName || '');
  const [email, setEmail] = useState(prefillContact?.contactEmail || '');
  const [phone, setPhone] = useState(prefillContact?.contactPhone || '');
  const [jobType, setJobType] = useState(prefillContact?.jobType || '');
  const [shootDate, setShootDate] = useState(prefillContact?.shootDate || '');
  const [revenue, setRevenue] = useState('');
  const [location, setLocation] = useState('');
  const [notes, setNotes] = useState('');
  const [selectedContactId, setSelectedContactId] = useState(prefillContact?.contactId || null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [saving, setSaving] = useState(false);

  const contactResults = useMemo(() => {
    if (!clientName || clientName.length < 1) return [];
    const q = clientName.toLowerCase();
    return allContacts.filter((c) =>
      (c.name || '').toLowerCase().includes(q) ||
      (c.company || '').toLowerCase().includes(q) ||
      (c.email || '').toLowerCase().includes(q)
    ).slice(0, 6);
  }, [clientName, allContacts]);

  const handleSelectContact = (contact) => {
    setSelectedContactId(contact.id);
    setClientName(contact.name || '');
    setEmail(contact.email || '');
    setPhone(contact.phone || '');
    setShowDropdown(false);
  };

  const handleRevenueChip = (val) => {
    setRevenue(String(val));
  };

  const handleSave = async () => {
    if (!clientName.trim()) { toast('Client name is required', 'error'); return; }
    if (!jobType) { toast('Job type is required', 'error'); return; }
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { toast('Invalid email format', 'error'); return; }

    setSaving(true);
    try {
      let contactId = selectedContactId;

      // If no existing contact matched, create a stub
      if (!contactId) {
        const stub = await createContact.mutateAsync({
          name: clientName.trim(),
          type: 'client',
          email: email.trim() || '',
          phone: phone.trim() || '',
          data: { incomplete: true },
        });
        contactId = stub.id;
      }

      // Auto-generate title
      const title = location
        ? `${jobType}: ${location.split(',')[0].trim()}`
        : `${jobType}: ${clientName.trim()}`;

      await createJob.mutateAsync({
        title,
        clientName: clientName.trim(),
        contactName: clientName.trim(),
        contactEmail: email.trim(),
        contactPhone: phone.trim(),
        contactId,
        jobType,
        shootDate: shootDate || null,
        shootDates: shootDate ? [shootDate] : [],
        days: shootDate ? '1' : '',
        revenue: revenue || '',
        location: location || '',
        notes: notes.trim() || '',
        status: 'intake',
        source: 'quick_add',
      });

      toast('Job created', 'success');
      onSave?.();
    } catch (err) {
      toast(`Error: ${err.message}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper sx={{ p: 3, maxHeight: '100%', overflowY: 'auto' }}>
      <Typography variant="h6" sx={{ mb: 0.5, fontFamily: '"Bebas Neue", sans-serif', letterSpacing: 0.5 }}>Quick Add Job</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2.5 }}>Fast entry — you can add more details later via Edit.</Typography>

      <Grid container spacing={2}>
        {/* Client name with autocomplete */}
        <Grid size={12}>
          <Box sx={{ position: 'relative' }}>
            <TextField
              fullWidth size="small"
              label={selectedContactId ? 'Client (Linked ✓)' : 'Client *'}
              value={clientName}
              onChange={(e) => {
                setClientName(e.target.value);
                setShowDropdown(true);
                if (selectedContactId) { setSelectedContactId(null); setEmail(''); setPhone(''); }
              }}
              onFocus={() => { if (clientName) setShowDropdown(true); }}
              onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
              placeholder="Start typing to search contacts..."
            />
            {showDropdown && contactResults.length > 0 && (
              <Paper elevation={4} sx={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10, maxHeight: 200, overflowY: 'auto' }}>
                <List dense sx={{ py: 0 }}>
                  {contactResults.map((c) => (
                    <ListItemButton key={c.id} data-track="job.contactPick" onMouseDown={() => handleSelectContact(c)} sx={{ py: 0.5 }}>
                      <ListItemText
                        primary={c.name}
                        secondary={[c.company, c.email].filter(Boolean).join(' · ')}
                        primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 600 }}
                        secondaryTypographyProps={{ fontSize: '0.7rem' }}
                      />
                    </ListItemButton>
                  ))}
                </List>
              </Paper>
            )}
            {clientName && !selectedContactId && contactResults.length === 0 && showDropdown && (
              <Typography variant="caption" color="warning.main" sx={{ mt: 0.25, display: 'block' }}>
                New contact — will be created automatically
              </Typography>
            )}
          </Box>
        </Grid>

        {/* Email + Phone */}
        <Grid size={6}>
          <TextField fullWidth size="small" label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </Grid>
        <Grid size={6}>
          <TextField fullWidth size="small" label="Phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
        </Grid>

        {/* Job Type */}
        <Grid size={12}>
          <FormControl fullWidth size="small">
            <InputLabel>Job Type *</InputLabel>
            <Select value={jobType} onChange={(e) => setJobType(e.target.value)} label="Job Type *">
              {JOB_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>

        {/* Shoot Date: presets + picker */}
        <Grid size={12}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.75, display: 'block' }}>Shoot Date</Typography>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
            {(() => {
              const today = new Date();
              const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
              const addDays = (n) => { const d = new Date(today); d.setDate(d.getDate() + n); return fmt(d); };
              const nextMon = () => { const d = new Date(today); d.setDate(d.getDate() + ((8 - d.getDay()) % 7 || 7)); return fmt(d); };
              const presets = [
                { label: 'Tomorrow', value: addDays(1) },
                { label: 'In 2 Days', value: addDays(2) },
                { label: 'In 3 Days', value: addDays(3) },
                { label: 'Next Week', value: nextMon() },
                { label: 'In 2 Weeks', value: addDays(14) },
                { label: 'Next Month', value: addDays(30) },
              ];
              return presets.map((p) => (
                <Chip
                  key={p.label}
                  label={p.label}
                  size="small"
                  onClick={() => setShootDate(p.value)}
                  color={shootDate === p.value ? 'primary' : 'default'}
                  variant={shootDate === p.value ? 'filled' : 'outlined'}
                  sx={{ fontSize: '0.65rem', height: 24, cursor: 'pointer' }}
                />
              ));
            })()}
          </Stack>
          <TextField fullWidth size="small" label="Or pick a date" type="date" value={shootDate} onChange={(e) => setShootDate(e.target.value)} slotProps={{ inputLabel: { shrink: true } }} />
        </Grid>

        {/* Revenue: chips + manual input */}
        <Grid size={12}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.75, display: 'block' }}>Revenue (ballpark)</Typography>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
            {REVENUE_PRESETS.map((v) => (
              <Chip
                key={v}
                label={fmtChip(v)}
                size="small"
                onClick={() => handleRevenueChip(v)}
                color={revenue === String(v) ? 'primary' : 'default'}
                variant={revenue === String(v) ? 'filled' : 'outlined'}
                sx={{ fontSize: '0.7rem', height: 26, cursor: 'pointer' }}
              />
            ))}
          </Stack>
          <TextField
            fullWidth size="small" label="Or enter amount" type="number"
            value={revenue} onChange={(e) => setRevenue(e.target.value)}
            slotProps={{ input: { inputProps: { min: 0, step: 0.01 } } }}
          />
        </Grid>

        {/* Location */}
        <Grid size={12}>
          <AddressAutocomplete variant="mui" label="Location (optional)" value={location} onChange={setLocation} />
        </Grid>

        {/* Notes */}
        <Grid size={12}>
          <TextField fullWidth size="small" label="Notes" multiline minRows={2} maxRows={4} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Any details about this job..." />
        </Grid>
      </Grid>

      {/* Actions */}
      <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button variant="outlined" onClick={onCancel} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>{saving ? 'Adding...' : 'Add Job'}</Button>
      </Stack>
    </Paper>
  );
}
